import React from "react";
import { Link } from "react-router-dom";
import DocsLayout, { Section, Steps, Callout, JumpNav, AvailableTo } from "./DocsLayout";

const Img = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} style={{ width: "100%", borderRadius: 8, border: "1px solid #e2e8f0", margin: "16px 0", display: "block" }} />
);

const COLOR = "#0ea5e9";
const ALL_ROLES = ["admin", "admin_assistant", "hr", "recruiter", "finance", "employee"] as const;

export default function GettingStarted() {
  return (
    <DocsLayout>
      <div style={{ marginBottom: 32 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: COLOR + "15", border: `1px solid ${COLOR}30`,
          borderRadius: 8, padding: "5px 14px", marginBottom: 16,
        }}>
          <span style={{ fontSize: 16 }}>👋</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLOR }}>Getting Started</span>
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>Getting Started</h1>
        <p style={{ fontSize: 15, color: "#64748b", maxWidth: 640, lineHeight: 1.6 }}>
          How to log in, secure your account, and get help from the AI chat assistant — every user on HRxAI
          starts here, regardless of role.
        </p>
      </div>

      <JumpNav color={COLOR} items={[
        { id: "whatis", label: "What is HRxAI?" },
        { id: "login", label: "Logging In" },
        { id: "chat", label: "AI Chat Assistant" },
        { id: "security", label: "Security & Account" },
      ]} />

      <Section id="whatis" title="What is HRxAI?">
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.8, marginBottom: 16 }}>
          HRxAI brings together recruitment, HR operations, payroll, and finance into one platform.
          Every user sees only the modules relevant to their role — keeping things simple and secure.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
          {[
            { icon: "🏢", label: "Multi-tenant", desc: "Fully isolated per company" },
            { icon: "🤖", label: "AI-powered", desc: "JD generation, CV scoring, HR chat" },
            { icon: "🔒", label: "Secure", desc: "TOTP, passkeys, encrypted storage" },
            { icon: "📧", label: "Email alerts", desc: "Approvals, payslips, invites" },
          ].map(f => (
            <div key={f.label} style={{
              background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
              padding: "14px 16px", display: "flex", gap: 12, alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 20 }}>{f.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{f.label}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section id="login" title="Logging In">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          There's no separate signup step — your company adds you, and you receive a one-time invite link
          by email.
        </p>
        <Steps items={[
          "Open the invite email and click the link — it's valid for a limited time, so use it soon after receiving it.",
          "Confirm your identity by entering your email address.",
          "Once confirmed, you're logged in — set up two-factor authentication or a passkey from Security (see below).",
          "For subsequent logins, go to https://www.hrxai.app and sign in with your credentials or passkey.",
        ]} />
        <Callout
          icon="⚠️"
          title="Link expired?"
          body="Invite and setup links expire after a set window. Ask your Admin or HR to resend the invite if yours no longer works."
        />
      </Section>

      <Section id="chat" title="AI Chat Assistant">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          The floating chat bubble in the bottom-right corner of every page lets you talk to Aria, HRxAI's
          AI assistant, in plain language — it sees your real HR data and can even take actions for you
          with your confirmation.
        </p>
        <Img src="/docs/screenshots/employee-chat.png" alt="AI Chat widget" />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7 }}>
          See the dedicated <Link to="/docs/ai-assistant" style={{ color: "#7c3aed", fontWeight: 600 }}>AI Chat Assistant</Link> page
          for what it knows by role, what actions it can perform, and usage limits.
        </p>
      </Section>

      <Section id="security" title="Security & Account">
        <AvailableTo roles={[...ALL_ROLES]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Every user has access to the Security page from their sidebar.
        </p>
        <Img src="/docs/screenshots/employee-security.png" alt="Security settings page" />
        <Steps items={[
          "Click Security in the sidebar.",
          "Change password: enter current password then new password twice.",
          "Enable TOTP: scan the QR code with Google Authenticator, then enter the 6-digit code to confirm.",
          "Add a Passkey: click Register Passkey and follow your browser's biometric prompt (Face ID, fingerprint, etc.).",
          "View active sessions from the same page.",
        ]} />
      </Section>
    </DocsLayout>
  );
}
